import $ from 'jquery';
import { select } from '@wordpress/data';
import { isPayoneerGatewaySelected } from './woocommerce';
import { PAYONEER_STORE_KEY } from '../store';

/**
 * Default selector that identifies the container of the "Place Order" button on the
 * Classic Checkout page.
 */
const DEFAULT_SELECTOR = '.form-row.place-order';
const BUTTON_SELECTOR = '#place_order';

const BODY_CLASS_STATE_READY = 'payoneer-checkout-payment-allowed';
const BODY_CLASS_STATE_INVALID = 'payoneer-checkout-payment-data-invalid';
const BODY_CLASS_STATE_PROCESSING = 'payoneer-checkout-payment-not-ready';

/**
 * Manages the checkout's "Place Order" section state with different blocking strategies:
 *
 * 1) BLOCK the Place Order button while components are loading (with spinner).
 *    → Prevent WooCommerce from creating "on-hold" orders due to missing payment details.
 *
 * 2) BLOCK the button after it was clicked - unblock it again on error (with spinner).
 *    → Prevent WooCommerce from creating duplicate orders for the same transaction.
 *
 * 3) DISABLE the button when payment form input is invalid (no spinner).
 *    → Prevent buyers from clicking the button until they completed the payment form.
 *
 * Dependency from WooCommerce:
 *   We use `$.fn.block()` and `$.fn.unblock()` methods provided by the "jQuery blockUI plugin"
 *   plugin, which is part of the WooCommerce core. This aligns with the approach taken by
 *   WooCommerce, and will display the familiar spinner animation while the button is blocked.
 */
class OrderPlacementManager {
	#selector = '';
	#blockingStyleElement = null;
	#message = null;
	#state = {
		/**
		 * Control the state of this manager. When not active, it does not
		 * block or disable the payment button.
		 */
		active: true,

		/**
		 * Set via API method to indicate a background activity is processing.
		 * While processing: A spinner is displayed.
		 */
		processing: false,

		/**
		 * Set via API method to indicate payment form contains valid input.
		 * While invalid: Button is disabled.
		 */
		valid: false,
	};

	constructor( selector = DEFAULT_SELECTOR ) {
		this.#selector = selector;
		this.#log( `initialized` );
		this.#updateDomState();
	}

	/**
	 * Get the current "Place Order" area element (fresh DOM query each time)
	 */
	get area() {
		return $( this.#selector );
	}

	/**
	 * Get the "Place Order" button element.
	 */
	get button() {
		return this.area.find( BUTTON_SELECTOR );
	}

	/**
	 * Check if payment should be allowed based on all conditions
	 */
	get isPaymentAllowed() {
		const { active, valid, processing } = this.#state;
		return !active || ( valid && !processing );
	}

	/**
	 * Check if checkout is blocked with a spinner.
	 * @depends on jquery.blockUI.js
	 */
	get isAreaBlocked() {
		// Managed by the "jQuery blockUI plugin" plugin. Value can be 1/0/undefined.
		return !!this.area.data( 'blockUI.isBlocked' );
	}

	/**
	 * Check if the payment button is currently disabled.
	 */
	get isButtonDisabled() {
		return this.button.prop( 'disabled' );
	}

	/**
	 * Checks if the payment form input is valid.
	 */
	get validPaymentInput() {
		return this.#state.valid;
	}

	/**
	 * Marks the payment form input as valid or invalid.
	 */
	setValidInput( isValid ) {
		this.#state.valid = isValid;
		this.#updateDomState();
	}

	/**
	 * Changes the "background process" state. An active background process
	 * locks the payment button behind a spinner.
	 */
	setProcessing( processing = true ) {
		if ( !processing ) {
			this.#message = null;
			this.#state.processing = false;
		} else {
			this.#state.processing = true;
		}

		this.#updateDomState();
	}

	setProcessingMessage( message ) {
		this.#message = message;
		this.#updateDomState();
	}

	setActive( state ) {
		this.#state.active = state;
		this.#updateDomState();
	}

	/**
	 * Update DOM state based on current conditions
	 * @private
	 */
	#updateDomState() {
		const { active, valid, processing } = this.#state;

		if ( !active ) {
			this.#unblockArea();
			this.#enableButton();
			this.#setBodyClass( null );
			return;
		}

		// BLOCK AREA: When components loading or processing (user waits)
		if ( processing ) {
			this.#setBodyClass( BODY_CLASS_STATE_PROCESSING );
			this.#blockArea();
			return;
		}

		// UNBLOCK AREA: Components ready and not processing
		this.#unblockArea();

		// DISABLE BUTTON: When input invalid (user can fix form)
		if ( !valid ) {
			this.#setBodyClass( BODY_CLASS_STATE_INVALID );
			this.#disableButton();
		} else {
			this.#setBodyClass( BODY_CLASS_STATE_READY );
			this.#enableButton();
		}
	}

	#setBodyClass( newClass ) {
		const STATES = [
			BODY_CLASS_STATE_READY,
			BODY_CLASS_STATE_INVALID,
			BODY_CLASS_STATE_PROCESSING,
		];

		STATES.forEach( state => {
			document.body.classList.remove( state );
		} );

		if ( newClass && STATES.includes( newClass ) ) {
			document.body.classList.add( newClass );
		}
	}

	/**
	 * Block the entire area with spinner
	 * @depends on jquery.blockUI.js
	 * @private
	 */
	#blockArea() {
		if ( 'function' !== typeof $.fn.block ) {
			return;
		}

		const args = {
			message: this.#message,
			overlayCSS: {
				background: '#fff',
				opacity: 0.6,
			}
		};

		// Customize the overlay when it contains a message/HTML elements.
		if ( this.#message ) {
			args.blockMsgClass = 'no-spinner';
			args.css = {
				maxWidth: '80%',
				width: 'auto',
				cursor: 'default',
			};
		}

		// No animation if we only update the blockUI overlay.
		if ( this.isAreaBlocked ) {
			args.fadeIn = 0;
		}

		this.#injectBlockingCSS();
		this.area.block( args );

		this.#log( '🔒 area blocked (spinner)' );
	}

	/**
	 * Unblock the area
	 * @depends on jquery.blockUI.js
	 * @private
	 */
	#unblockArea() {
		if ( !this.isAreaBlocked || 'function' !== typeof $.fn.unblock ) {
			return;
		}

		this.#removeBlockingCSS();
		this.area.unblock();
		this.#log( '🔓 area unblocked' );
	}

	/**
	 * Disable just the button
	 * @private
	 */
	#disableButton() {
		if ( this.isButtonDisabled ) {
			return;
		}

		this.button.prop( 'disabled', true ).addClass( 'disabled' );
		this.#log( '❌ button disabled' );
	}

	/**
	 * Enable the button
	 * @private
	 */
	#enableButton() {
		if ( !this.isButtonDisabled ) {
			return;
		}

		this.button.prop( 'disabled', false ).removeClass( 'disabled' );
		this.#log( '✅ button enabled' );
	}

	/**
	 * Inject CSS to disable area interactions
	 * @private
	 */
	#injectBlockingCSS() {
		if ( this.#blockingStyleElement ) {
			return;
		}

		this.#blockingStyleElement = document.createElement( 'style' );
		this.#blockingStyleElement.textContent = this.#getBlockingCSS();

		document.head.appendChild( this.#blockingStyleElement );
		this.#log( '🎨 blocking CSS injected' );
	}

	/**
	 * Remove injected blocking CSS
	 * @private
	 */
	#removeBlockingCSS() {
		if ( !this.#blockingStyleElement ) {
			return;
		}

		this.#blockingStyleElement.remove();
		this.#blockingStyleElement = null;
		this.#log( '🎨 blocking CSS removed' );
	}

	/**
	 * Generate CSS rules for blocking the "Pay" button on browser-level.
	 * These rules are used during the early page load phase, while the SDK is still loading.
	 * @private
	 */
	#getBlockingCSS() {
		const baseSelector = this.#selector;

		return `
			${baseSelector} .place-order {
				pointer-events: none !important;
			}
			${baseSelector} #place_order {
				opacity: 0.5 !important;
				pointer-events: none !important;
			}
			.blockUI.no-spinner:before {
				display: none !important;
			}
			.blockUI.no-spinner button {
				display: block;
				width: 100%;
			}
		`;
	}

	/**
	 * Log with consistent formatting and state visibility
	 * @private
	 */
	#log( message ) {
		const { active, valid, processing } = this.#state;
		console.log( `[OrderPlacement] ${message} | A:${active} V:${valid} P:${processing}` );
	}
}

export default OrderPlacementManager;
